/**
 * Grade × Board helpers for the shared curriculum views.
 *
 * `module.gradeBoards` is the authoritative store for "at what grade does each board
 * teach this topic" — rows of [grade, boards[]], grouped by grade. These restate
 * Backend/functions/src/shared/curriculum/gradeResolution.ts over the widened string
 * types the shared views use (see ./types.ts).
 *
 * See docs/FeatureDesigns/question-pools-and-entitlement/question-grade-board-model-vision.md.
 */
export type GradeBoards = [string, string[]][];

/** All grades at which `board` teaches this topic (empty ⇒ no tuple mentions it). */
export function gradesForBoard(gradeBoards: GradeBoards | undefined, board: string): string[] {
  return (gradeBoards ?? []).filter(([, boards]) => boards.includes(board)).map(([g]) => g);
}

/**
 * How a canonical node's tuples relate to one curriculum's (board, grade):
 *  - `none`       no tuple mentions this board ⇒ nothing asserted, grade falls back to question.class
 *  - `match`      this board teaches it at this curriculum's grade (or the curriculum is gradeless)
 *  - `otherGrade` this board teaches it, but only at other grade(s) — it belongs to a different
 *                 grade-curriculum of the same board
 */
export interface GradeBoardFit {
  kind: 'none' | 'match' | 'otherGrade';
  /** Grades this board teaches the topic at, per the tuples. */
  grades: string[];
}

export function gradeBoardFit(
  gradeBoards: GradeBoards | undefined,
  board: string,
  grade?: string,
): GradeBoardFit {
  const grades = gradesForBoard(gradeBoards, board);
  if (!grades.length) return { kind: 'none', grades };
  // Gradeless curricula (JEE/NEET exam tracks) can't mismatch on grade.
  if (!grade || grades.includes(grade)) return { kind: 'match', grades };
  return { kind: 'otherGrade', grades };
}

/** "10: CBSE, STATE · 9: ICSE" — compact, for table cells. */
export function formatGradeBoards(gradeBoards: GradeBoards | undefined): string {
  return (gradeBoards ?? []).map(([g, boards]) => `${g}: ${boards.join(', ')}`).join(' · ');
}
